import {
	Breakpoints,
	Container,
	ContainerPadding,
	CTA,
	Image,
	Spinner,
	Text,
	TextAlignment,
	TokenTextAppearance,
} from '@volkswagen-onehub/components-core';
import { LayerHandleV2 } from '@volkswagen-onehub/layer-manager';
import * as React from 'react';
import { DataVehicles, ErrorType, Vehicle, VehicleStatus } from '@esportal/portal-service';
import { useTrackingManager } from '../../../tracking/useTrackingManager';
import { formatOwnership } from '../../../tracking/trackingHelper';
import { PortalContext } from '../../../context/PortalContext';

export const DeleteVehicle: React.FC<{
	vehicleToDelete: Vehicle | null;
	vehicles: Vehicle[] | null;
	setDeleteProcess: any;
	setIsJourneyLayerVisible: React.Dispatch<React.SetStateAction<boolean>>;
	setIsAlertLayerVisible: React.Dispatch<React.SetStateAction<boolean>>;
	layerRef: React.RefObject<LayerHandleV2<any>>;
	setErrorType: React.Dispatch<React.SetStateAction<ErrorType>>;
}> = ({
	vehicleToDelete,
	vehicles,
	setDeleteProcess,
	setIsJourneyLayerVisible,
	setIsAlertLayerVisible,
	layerRef,
	setErrorType,
}) => {
	const { portalService } = React.useContext(PortalContext);
	const [deleteCompleted, setDeleteCompleted] = React.useState<boolean>(false);
	const [isDeleteing, setIsDeleteing] = React.useState<string | null>(null);

	const trackingManager = useTrackingManager();

	const handleCancelOnClick = (vehicle: Vehicle) => {
		trackingManager.trackClubVWButtonClick(
			'Cancelar',
			{ contentId: `Cancel delete vehicle - ${vehicle.comercialDenomination}` },
			{ vehicle: formatOwnership([vehicle]) },
		);
		setDeleteProcess(false);
	};

	const handleDelete = (vehicle: Vehicle) => {
		trackingManager.trackClubVWButtonClick(
			'No lo conduzco',
			{ contentId: `Confirm delete vehicle - ${vehicle.comercialDenomination}` },
			{ vehicle: formatOwnership([vehicle]) },
		);

		setIsDeleteing(vehicle.frame);

		portalService.deletePersonVehicle(vehicle.frame).subscribe({
			next: (updatedVehicles: DataVehicles | null) => {
				if (updatedVehicles && vehicleToDelete) {
					vehicleToDelete.status = VehicleStatus.TO_BE_DELETED;
					setDeleteCompleted(true);
				}

				setIsDeleteing(null);
			},
			error: () => {
				setIsJourneyLayerVisible(false);
				setIsAlertLayerVisible(true);
				setErrorType(ErrorType.DeleteVehicle);

				if (layerRef?.current) {
					layerRef?.current.close();
					(layerRef.current as any) = null;
				}

				setIsDeleteing(null);
			},
		});
	};

	const handleVolverOnclick = () => {
		trackingManager.trackClubVWButtonClick(
			'Volver',
			{ contentId: 'Back to Person Vehicles layer' },
			{ vehicle: formatOwnership(vehicles) },
		);
		setDeleteProcess(false);
	};

	return (
		<>
			{!deleteCompleted && vehicleToDelete ? (
				<>
					<Container horizontalAlign={'center'} wrap={'always'}>
						<Container
							horizontalAlign={'center'}
							padding={{
								right: {
									[Breakpoints.default]: ContainerPadding.grid001,
									[Breakpoints.b960]: ContainerPadding.grid002,
								},
								bottom: ContainerPadding.dynamic0200,
								left: {
									[Breakpoints.default]: ContainerPadding.grid001,
									[Breakpoints.b960]: ContainerPadding.grid002,
								},
							}}
						>
							<Text appearance={TokenTextAppearance.headline300} textAlign={TextAlignment.center}>
								¿Ya no conduces este {vehicleToDelete.comercialDenomination} {vehicleToDelete.frame}?
							</Text>
						</Container>
						<Image
							src={
								vehicleToDelete?.mediaAssets?.Front_Left ??
								'https://vgedonehubappsstaweupro.blob.core.windows.net/storage/images-portal/car_silhouette.png'
							}
							height="200px"
							alt="VWCar"
							objectFit="contain"
						/>
					</Container>

					<Container
						horizontalAlign={'center'}
						padding={{
							top: ContainerPadding.dynamic0200,
							bottom: ContainerPadding.dynamic0200,
						}}
					>
						<Container
							padding={{
								right: ContainerPadding.dynamic0050,
							}}
						>
							<CTA
								tag="button"
								emphasis="secondary"
								onClick={() => {
									handleCancelOnClick(vehicleToDelete);
								}}
								aria-label="clickDelete"
							>
								Cancelar
							</CTA>
						</Container>
						{isDeleteing ? (
							<CTA tag="button" emphasis="primary" disabled={true} aria-label="clickConfirm">
								<Spinner />
							</CTA>
						) : (
							<CTA
								tag="button"
								emphasis="primary"
								onClick={() => handleDelete(vehicleToDelete)}
								aria-label="clickConfirm"
							>
								<Text>No lo conduzco</Text>
							</CTA>
						)}
					</Container>
				</>
				) : (
					<Container horizontalAlign={'center'} verticalAlign={'center'}>
						{(() => {
							trackingManager.trackClubVWInfoMessageLoad({ contentId: 'Deleting vehicle' });
							return null;
						})()}
						<Container horizontalAlign={'center'} verticalAlign={'center'}>
						<Container
							wrap={'always'}
							padding={{
								right: {
									[Breakpoints.default]: ContainerPadding.static100,
									[Breakpoints.b560]: ContainerPadding.grid001,
									[Breakpoints.b960]: ContainerPadding.grid002,
								},
								left: {
									[Breakpoints.default]: ContainerPadding.static100,
									[Breakpoints.b560]: ContainerPadding.grid001,
									[Breakpoints.b960]: ContainerPadding.grid002,
								},
							}}
						>
							<Text appearance={TokenTextAppearance.headline300} textAlign={TextAlignment.center}>
								¡Listo! Estamos eliminando este Volkswagen de tu perfil.
							</Text>
						</Container>
					</Container>
					<Container
						padding={{
							top: ContainerPadding.dynamic0200,
							bottom: ContainerPadding.dynamic0200,
						}}
						horizontalAlign={'center'}
						verticalAlign={'center'}
					>
						<CTA
							tag="button"
							emphasis="primary"
							onClick={() => vehicleToDelete && handleVolverOnclick()}
							aria-label="clickConfirm"
						>
							Volver
						</CTA>
					</Container>
				</Container>
			)}
		</>
	);
};
